import type { CohortConditionGroup, CohortRuleConditions } from "@unbox/api";
import { AlertTriangle, ArrowLeft, HelpCircle, Plus } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { useCohortRules, useCreateCohortRule, useUpdateCohortRule } from "@/api/cohort-rules";
import { SlideoutMenu } from "@/components/application/slideout-menus/slideout-menu";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { cx } from "@/utils/cx";
import { FIELD_OPTIONS } from "../condition-fields";
import {
  makeConditionGroup,
  type RuleDraftField,
  toCreateRulePayload,
  toUpdateRulePayload,
  useRuleDraft,
} from "../use-rule-draft";
import { CohortTargetSelect } from "./cohort-target-select";
import { ConditionGroupCard } from "./condition-group-card";
import { ExemptCreatorList } from "./exempt-creator-list";
import { ImpactPreview } from "./impact-preview";

interface RuleBuilderDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruleId?: string;
  onBack?: () => void;
  onCohortsChanged: () => void;
  onCreated?: () => void;
}

export function RuleBuilderDrawer({
  open,
  onOpenChange,
  ruleId,
  onBack,
  onCohortsChanged,
  onCreated,
}: RuleBuilderDrawerProps) {
  const isEditing = !!ruleId;
  const { data: rulesData } = useCohortRules();
  const createRule = useCreateCohortRule();
  const updateRule = useUpdateCohortRule();
  const { form, conditions, exceptGroup, setExceptGroup, hydrate, missingField, canSave } = useRuleDraft();
  const fieldRefs = {
    name: useRef<HTMLDivElement>(null),
    when: useRef<HTMLDivElement>(null),
    except: useRef<HTMLDivElement>(null),
    cohortId: useRef<HTMLDivElement>(null),
  } satisfies Record<RuleDraftField, unknown>;

  // distinctProductsSold and distinctSkusSold are both derived from
  // sample_fulfillments.paidOrderCount, the same field currently affected by a TikTok sync bug
  // behind Total GMV in Collaborations — one flag covers all symptoms of that same root cause.
  const paidOrderCountDataHidden = useFeatureFlag("F-total-gmv-hidden-collab-groups");
  const fieldOptions = useMemo(
    () =>
      paidOrderCountDataHidden
        ? FIELD_OPTIONS.filter((o) => o.id !== "distinctProductsSold" && o.id !== "distinctSkusSold")
        : FIELD_OPTIONS,
    [paidOrderCountDataHidden],
  );

  const name = useWatch({ control: form.control, name: "name" });
  const cohortId = useWatch({ control: form.control, name: "cohortId" });
  const isActive = useWatch({ control: form.control, name: "isActive" });
  const unassignAlreadyAssigned = useWatch({ control: form.control, name: "unassignAlreadyAssigned" });

  const [hydratedKey, setHydratedKey] = useState<string | undefined>(undefined);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowDiscardConfirm(false);
      setSaveAttempted(false);
    }
  }, [open]);

  // Hydrates once per (open, ruleId) pair, waiting for `rulesData` to contain the row
  // when editing. Resetting `hydratedKey` on close guarantees the next open — whether
  // the same rule, a different rule, or a fresh "New Rule" — hydrates from scratch.
  useEffect(() => {
    if (!open) {
      setHydratedKey(undefined);
      return;
    }
    const key = ruleId ?? "__new__";
    if (hydratedKey === key) return;
    if (!ruleId) {
      hydrate(undefined);
      setHydratedKey(key);
      return;
    }
    const rule = rulesData?.items.find((r) => r.ruleId === ruleId);
    if (!rule) return;
    hydrate({
      name: rule.name,
      cohortId: rule.cohortId,
      conditions: rule.conditions as unknown as CohortRuleConditions,
      isActive: rule.isActive,
    });
    setHydratedKey(key);
  }, [open, ruleId, rulesData, hydratedKey, hydrate]);

  const whenGroups = conditions?.when ?? [];
  const setWhenGroups = (groups: CohortConditionGroup[]) =>
    form.setValue("conditions.when", groups, { shouldDirty: true, shouldValidate: true });

  const assignedCount = rulesData?.items.find((r) => r.ruleId === ruleId)?.assignedCount ?? 0;
  const isDirty = form.formState.isDirty;

  const handleSave = async () => {
    if (missingField) {
      setSaveAttempted(true);
      fieldRefs[missingField].current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const values = form.getValues();
    if (isEditing && ruleId) {
      await updateRule.mutateAsync({ ruleId, ...toUpdateRulePayload(values) });
    } else {
      await createRule.mutateAsync(toCreateRulePayload(values));
      onCreated?.();
    }
    onCohortsChanged();
    onOpenChange(false);
  };

  const handleBack = onBack ?? (() => onOpenChange(false));

  const requestClose = () => {
    if (showDiscardConfirm) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onOpenChange(false);
    }
  };

  const handleBackRequest = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      handleBack();
    }
  };

  return (
    <SlideoutMenu
      isOpen={open}
      onOpenChange={(next) => {
        if (!next) requestClose();
      }}
      isDismissable
    >
      <SlideoutMenu.Header onClose={requestClose}>
        <h2 className="text-lg font-semibold text-primary">{isEditing ? "Edit Rule" : "New Rule"}</h2>
      </SlideoutMenu.Header>

      {showDiscardConfirm ? (
        <>
          <SlideoutMenu.Content className="flex flex-col items-center gap-0 pt-16">
            <div className="flex w-full flex-col items-center gap-4 rounded-2xl border border-secondary bg-unbox-cream-50 px-8 pt-4 pb-8">
              <div className="flex size-16 items-center justify-center rounded-full">
                <AlertTriangle className="size-6 text-warning-500" />
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-xl font-semibold text-primary">Unsaved changes</p>
                <p className="w-[360px] text-md text-tertiary">
                  Your changes will be permanently lost if you don't save them.
                </p>
              </div>
            </div>
          </SlideoutMenu.Content>

          <SlideoutMenu.Footer className="flex flex-row items-center gap-6">
            <Button
              color="tertiary"
              className="flex-1"
              onClick={() => {
                setShowDiscardConfirm(false);
                handleBack();
              }}
            >
              Discard Changes
            </Button>
            <Button color="secondary" className="flex-1" onClick={() => setShowDiscardConfirm(false)}>
              Keep Editing
            </Button>
            <Button
              color="primary"
              className="w-[184px]"
              isDisabled={!canSave}
              isLoading={createRule.isPending || updateRule.isPending}
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </SlideoutMenu.Footer>
        </>
      ) : (
        <>
          <SlideoutMenu.Content className="flex flex-col gap-6">
            <div className="flex flex-row items-end gap-4">
              <div ref={fieldRefs.name} className="flex-1">
                <Input
                  label="Rule Name"
                  placeholder="Name this rule"
                  value={name}
                  isInvalid={saveAttempted && missingField === "name"}
                  onChange={(value) => form.setValue("name", value, { shouldDirty: true, shouldValidate: true })}
                />
              </div>
              <Toggle
                isSelected={isActive}
                onChange={(checked) => form.setValue("isActive", checked, { shouldDirty: true })}
                label={isActive ? "Rule Enabled" : "Rule Disabled"}
              />
            </div>

            <div inert={!isActive} className={cx("flex flex-col gap-8", !isActive && "opacity-[0.58]")}>
              <div ref={fieldRefs.when} className="flex flex-col gap-3">
                <span className="text-xs font-semibold tracking-wide text-secondary uppercase">
                  When creator matches
                </span>
                {whenGroups.map((group, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: groups have no stable id, index is fine for a locally-edited draft list
                  <div key={index} className="flex flex-col gap-2">
                    {index > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-secondary" />
                        <span className="text-xs font-semibold text-secondary">AND</span>
                        <div className="h-px flex-1 bg-secondary" />
                      </div>
                    )}
                    <ConditionGroupCard
                      group={group}
                      fieldOptions={fieldOptions}
                      onChange={(updated) => setWhenGroups(whenGroups.map((g, i) => (i === index ? updated : g)))}
                      onRemoveGroup={
                        whenGroups.length > 1
                          ? () => setWhenGroups(whenGroups.filter((_, i) => i !== index))
                          : undefined
                      }
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-xl border border-secondary bg-unbox-cream-50 p-3 text-sm font-medium text-secondary hover:text-primary hover:border-primary transition-colors"
                  onClick={() => setWhenGroups([...whenGroups, makeConditionGroup()])}
                >
                  <Plus className="size-4 text-quaternary" />
                  Add group logic
                </button>
              </div>

              <div ref={fieldRefs.except} className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold tracking-wide text-secondary uppercase">Except when</span>
                  <div className="h-px flex-1 bg-border-secondary" />
                </div>
                {exceptGroup ? (
                  <ConditionGroupCard
                    group={exceptGroup}
                    fieldOptions={fieldOptions}
                    onChange={setExceptGroup}
                    onRemoveGroup={() => setExceptGroup(null)}
                    dashed
                  />
                ) : (
                  <button
                    type="button"
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-secondary bg-unbox-cream-50 p-3 text-sm font-medium text-secondary hover:text-primary hover:border-primary transition-colors"
                    onClick={() => setExceptGroup(makeConditionGroup())}
                  >
                    <Plus className="size-4 text-quaternary" />
                    Add Exception
                  </button>
                )}

                <div className="flex items-center justify-start gap-2 mt-4">
                  <span className="text-xs font-semibold text-secondary">OR</span>
                  <div className="h-px flex-1 bg-border-secondary" />
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold tracking-wide text-secondary uppercase">
                    They're individually exempt
                  </span>
                  <Tooltip
                    title="These creators are always exempt, even if they match the rule's conditions."
                    placement="top"
                  >
                    <TooltipTrigger>
                      <HelpCircle className="size-3.5 text-quaternary cursor-help" />
                    </TooltipTrigger>
                  </Tooltip>
                </div>
                <ExemptCreatorList onCohortsChanged={onCohortsChanged} />
              </div>

              <div ref={fieldRefs.cohortId} className="flex flex-col gap-3">
                <span className="text-xs font-semibold tracking-wide text-secondary uppercase">Then</span>
                <CohortTargetSelect
                  value={cohortId}
                  onChange={(id) => form.setValue("cohortId", id, { shouldDirty: true, shouldValidate: true })}
                  isDisabled={isEditing}
                  isInvalid={saveAttempted && missingField === "cohortId"}
                />
              </div>
            </div>
          </SlideoutMenu.Content>

          <SlideoutMenu.Footer className="flex flex-col gap-3">
            {isActive ? (
              conditions && <ImpactPreview conditions={conditions} />
            ) : (
              <div className="flex flex-col gap-3 rounded-xl border border-warning-200 bg-warning-50 p-4">
                <p className="text-sm text-secondary">
                  From now on, creators won't be assigned to this cohort by this rule. Anyone it already assigned keeps
                  their cohort.
                </p>
                <div className="h-px bg-warning-200" />
                <Checkbox
                  size="sm"
                  isSelected={assignedCount === 0 ? false : unassignAlreadyAssigned}
                  isDisabled={assignedCount === 0}
                  onChange={(checked) => form.setValue("unassignAlreadyAssigned", checked, { shouldDirty: true })}
                  label={
                    assignedCount === 0
                      ? "No creators are currently assigned by this rule."
                      : `Also unassign the ${assignedCount} creators already assigned by this rule.`
                  }
                />
              </div>
            )}
            <div className="flex flex-row justify-between gap-3">
              <Button color="secondary" iconLeading={ArrowLeft} onClick={handleBackRequest}>
                Back
              </Button>
              <Button color="primary" isLoading={createRule.isPending || updateRule.isPending} onClick={handleSave}>
                Save
              </Button>
            </div>
          </SlideoutMenu.Footer>
        </>
      )}
    </SlideoutMenu>
  );
}
